import { computed, getCurrentScope, onScopeDispose, ref, type ComputedRef } from 'vue'

import type { PlatformActionRequest } from '../models/platformAction.types'
import { isTerminalStatus } from '../service/actionStatus'

/** README §Flow step 4: poll the detail endpoint until the status is terminal. */
export const POLL_INTERVAL_MS = 3_000
/** ~2 minutes of polling. A request still `executing` after that is watched by a manual refresh. */
export const POLL_MAX_TICKS = 40

export interface ActionPollingDeps {
  /** One silent detail load. Returns `null` when the load was aborted or failed — polling stops. */
  load: (workspaceId: number, actionId: string) => Promise<PlatformActionRequest | null>
}

export interface UseActionPollingOptions {
  intervalMs?: number
  maxTicks?: number
  /** Injected so specs drive ticks with fake timers instead of real time. */
  schedule?: (callback: () => void, ms: number) => number
  cancel?: (handle: number) => void
}

export interface UseActionPolling {
  isPolling: ComputedRef<boolean>
  /** True once the tick budget ran out with the request still non-terminal. */
  exhausted: ComputedRef<boolean>
  start: (workspaceId: number, actionId: string) => void
  stop: () => void
}

/**
 * Polls one request until it reaches a terminal status.
 *
 * The caller loads the detail once when opening it; this only owns the follow-up ticks, and every
 * exit path (terminal status, failed tick, tick budget, `stop()`, scope dispose) clears the timer —
 * a drawer that closes mid-execution must not keep hitting the API.
 */
export function useActionPolling(
  deps: ActionPollingDeps,
  options: UseActionPollingOptions = {},
): UseActionPolling {
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS
  const maxTicks = options.maxTicks ?? POLL_MAX_TICKS
  const schedule = options.schedule ?? ((callback, ms) => window.setTimeout(callback, ms))
  const cancel = options.cancel ?? ((handle) => window.clearTimeout(handle))

  const polling = ref(false)
  const exhausted = ref(false)
  let handle: number | null = null
  let ticks = 0
  // Generation counter: a `start()` for another request must invalidate an in-flight tick, or the
  // old request's response would schedule ticks against the new one.
  let generation = 0

  function stop(): void {
    if (handle !== null) {
      cancel(handle)
      handle = null
    }
    polling.value = false
    generation += 1
  }

  function start(workspaceId: number, actionId: string): void {
    stop()
    ticks = 0
    exhausted.value = false
    polling.value = true
    queue(workspaceId, actionId, generation)
  }

  function queue(workspaceId: number, actionId: string, forGeneration: number): void {
    handle = schedule(() => {
      void tick(workspaceId, actionId, forGeneration)
    }, intervalMs)
  }

  async function tick(workspaceId: number, actionId: string, forGeneration: number): Promise<void> {
    if (forGeneration !== generation) return
    ticks += 1

    const request = await deps.load(workspaceId, actionId)
    if (forGeneration !== generation) return

    if (!request || isTerminalStatus(request.status)) {
      stop()
      return
    }
    if (ticks >= maxTicks) {
      exhausted.value = true
      stop()
      return
    }
    queue(workspaceId, actionId, forGeneration)
  }

  if (getCurrentScope()) onScopeDispose(stop)

  return {
    isPolling: computed(() => polling.value),
    exhausted: computed(() => exhausted.value),
    start,
    stop,
  }
}
